import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchProjectGuardrails,
  fetchProjectRepos,
  updateProjectGuardrails,
} from '../../lib/api/projects';
import {
  GUARDRAILS_CAP_BYTES,
  type GuardrailsView,
} from '../../../../shared/types/projects';

/**
 * FR-6 — GUARDRAILS editor.
 *
 * Project-level text on top, optional per-repo overlay below (read-only here
 * — per-repo guardrails are edited on the membership row alongside its
 * overrides). Live byte counter against the combined 8 KB cap so the
 * operator sees when their text will be truncated for the agent.
 */
export const GuardrailsTab: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [selectedRepo, setSelectedRepo] = useState<string>('');
  const reposQ = useQuery({
    queryKey: ['project', projectId, 'repos'],
    queryFn: () => fetchProjectRepos(projectId),
  });
  const guardrailsQ = useQuery({
    queryKey: ['project', projectId, 'guardrails', selectedRepo],
    queryFn: () =>
      fetchProjectGuardrails(projectId, selectedRepo ? { repo: selectedRepo } : {}),
  });

  if (guardrailsQ.isLoading) {
    return <div className="text-sm text-gray-500">Loading guardrails…</div>;
  }
  if (guardrailsQ.isError) {
    return <div role="alert" className="text-sm text-red-600">{(guardrailsQ.error as Error).message}</div>;
  }
  if (!guardrailsQ.data) return null;

  return (
    <GuardrailsEditor
      projectId={projectId}
      initial={guardrailsQ.data}
      repos={reposQ.data ?? []}
      selectedRepo={selectedRepo}
      onSelectRepo={setSelectedRepo}
      onSaved={() => guardrailsQ.refetch()}
    />
  );
};

const GuardrailsEditor: React.FC<{
  projectId: string;
  initial: GuardrailsView;
  repos: Array<{ fullName: string }>;
  selectedRepo: string;
  onSelectRepo: (s: string) => void;
  onSaved: () => void;
}> = ({ projectId, initial, repos, selectedRepo, onSelectRepo, onSaved }) => {
  const [text, setText] = useState(initial.text);
  const [saveError, setSaveError] = useState<string | null>(null);

  const projectBytes = useMemo(
    () => new TextEncoder().encode(text).length,
    [text],
  );
  const overlayBytes = useMemo(
    () => new TextEncoder().encode(initial.repoOverlay?.text ?? '').length,
    [initial.repoOverlay],
  );
  // Account for the comment headers + separator that concatGuardrails emits.
  // Rough approximation is enough for the badge.
  const combinedApprox = projectBytes + overlayBytes + 60;
  const overCap = combinedApprox > GUARDRAILS_CAP_BYTES;

  const mutation = useMutation({
    mutationFn: (guardrails: string) =>
      updateProjectGuardrails(projectId, { guardrails }),
    onSuccess: () => {
      setSaveError(null);
      onSaved();
    },
    onError: (e) => setSaveError((e as Error).message),
  });

  const isDirty = text !== initial.text;

  return (
    <div className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Last edited {formatTimestamp(initial.updatedAt)}
          {initial.updatedBy && <> by <span className="font-mono">{initial.updatedBy}</span></>}
        </div>
        <div className="flex items-center gap-3">
          <ByteBadge
            bytes={combinedApprox}
            cap={GUARDRAILS_CAP_BYTES}
            overCap={overCap}
          />
          <button
            type="button"
            onClick={() => mutation.mutate(text)}
            disabled={!isDirty || mutation.isPending}
            data-testid="save-guardrails"
            className="bg-blue-600 text-white text-sm px-3 py-1 rounded disabled:opacity-40 hover:bg-blue-700"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4">
        <section>
          <header className="text-xs font-medium text-gray-700 mb-1">
            Project guardrails (markdown)
          </header>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck
            placeholder="# Project-level conventions, runbooks, do-not-do rules..."
            data-testid="guardrails-textarea"
            className="block w-full border rounded p-2 font-mono text-xs"
            rows={20}
          />
        </section>
        <section>
          <header className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-700">Live preview</span>
            <PreviewRepoPicker
              repos={repos}
              value={selectedRepo}
              onChange={onSelectRepo}
            />
          </header>
          <div
            className="border rounded p-3 bg-gray-50 text-xs h-[440px] overflow-auto whitespace-pre-wrap font-mono"
            data-testid="guardrails-preview"
          >
            {text || <span className="text-gray-400">(empty)</span>}
            {initial.repoOverlay && (
              <>
                <div className="text-[10px] text-gray-500 mt-3 mb-1">
                  ⤵ repo overlay: {initial.repoOverlay.fullName}
                </div>
                <div className="bg-purple-50 border-l-2 border-purple-300 pl-2">
                  {initial.repoOverlay.text || (
                    <span className="text-gray-400">(no per-repo guardrails set)</span>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {saveError && (
        <p role="alert" className="text-xs text-red-600">{saveError}</p>
      )}
      {overCap && (
        <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
          Combined byte count exceeds the {GUARDRAILS_CAP_BYTES} byte cap.
          The orchestrator will truncate from the end before passing to the
          agent — split content across project + per-repo or shorten it.
        </p>
      )}
    </div>
  );
};

const ByteBadge: React.FC<{ bytes: number; cap: number; overCap: boolean }> = ({
  bytes, cap, overCap,
}) => (
  <span
    data-testid="bytes-badge"
    className={
      'text-xs px-2 py-0.5 rounded font-mono ' +
      (overCap
        ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
        : 'bg-gray-100 text-gray-700 border')
    }
  >
    {bytes} / {cap} B
  </span>
);

const PreviewRepoPicker: React.FC<{
  repos: Array<{ fullName: string }>;
  value: string;
  onChange: (v: string) => void;
}> = ({ repos, value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    aria-label="Preview with repo overlay"
    className="text-[10px] border rounded px-1 py-0.5 bg-white"
  >
    <option value="">project only</option>
    {repos.map((r) => (
      <option key={r.fullName} value={r.fullName}>
        + {r.fullName}
      </option>
    ))}
  </select>
);

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default GuardrailsTab;
